/**
 * Pure math/geometry utilities for marker dispersal.
 * No DOM or library dependencies — operates entirely in pixel-coordinate space.
 */

/**
 * Calculate the geometric centroid (mean) of an array of points.
 * @param {{ x: number, y: number }[]} points
 * @returns {{ x: number, y: number }}
 */
export function centroid(points) {
  if (points.length === 0) {
    return { x: 0, y: 0 };
  }
  let sumX = 0;
  let sumY = 0;
  for (const p of points) {
    sumX += p.x;
    sumY += p.y;
  }
  return {
    x: sumX / points.length,
    y: sumY / points.length,
  };
}

/**
 * Calculate the angle (in radians) from point p1 to point p2.
 * Returns a value in [0, 2π). Angle 0 is east (+x direction),
 * π/2 is south (+y direction in screen coordinates).
 * @param {{ x: number, y: number }} p1
 * @param {{ x: number, y: number }} p2
 * @returns {number}
 */
export function angleBetween(p1, p2) {
  const angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
  return angle < 0 ? angle + 2 * Math.PI : angle;
}

/**
 * Compute the bounding rectangle for a marker given its anchor position,
 * anchor offset within the icon, and the icon dimensions.
 *
 * The anchor point (x, y) is the position on the map. The icon extends
 * from (x - anchorX, y - anchorY) to (x - anchorX + width, y - anchorY + height).
 *
 * @param {{ x: number, y: number, width: number, height: number, anchorX: number, anchorY: number }} marker
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number }}
 */
export function markerBounds(marker) {
  return {
    minX: marker.x - marker.anchorX,
    minY: marker.y - marker.anchorY,
    maxX: marker.x - marker.anchorX + marker.width,
    maxY: marker.y - marker.anchorY + marker.height,
  };
}

/**
 * Check whether two axis-aligned rectangles overlap (or are within
 * `spacing` pixels of each other).
 *
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} r1
 * @param {{ minX: number, minY: number, maxX: number, maxY: number }} r2
 * @param {number} [spacing=0] - Additional gap required between rectangles
 * @returns {boolean}
 */
export function rectsOverlap(r1, r2, spacing = 0) {
  return !(
    r1.maxX + spacing <= r2.minX ||
    r2.maxX + spacing <= r1.minX ||
    r1.maxY + spacing <= r2.minY ||
    r2.maxY + spacing <= r1.minY
  );
}

/**
 * Compute a point on a circle given center, radius, and angle.
 * @param {{ x: number, y: number }} center
 * @param {number} radius
 * @param {number} angle - in radians
 * @returns {{ x: number, y: number }}
 */
export function positionOnCircle(center, radius, angle) {
  return {
    x: center.x + radius * Math.cos(angle),
    y: center.y + radius * Math.sin(angle),
  };
}

/**
 * Build a bounding rect for a marker placed at a given position.
 * @param {{ width: number, height: number, anchorX: number, anchorY: number }} marker
 * @param {{ x: number, y: number }} position
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number }}
 */
export function boundsAtPosition(marker, position) {
  return {
    minX: position.x - marker.anchorX,
    minY: position.y - marker.anchorY,
    maxX: position.x - marker.anchorX + marker.width,
    maxY: position.y - marker.anchorY + marker.height,
  };
}

/**
 * Check whether any pair of markers in the given list overlaps when each
 * is placed at the specified positions.
 *
 * @param {{ width: number, height: number, anchorX: number, anchorY: number }[]} markers
 * @param {{ x: number, y: number }[]} positions - same length/order as markers
 * @param {number} spacing
 * @returns {boolean} true if there is at least one overlap
 */
export function anyOverlap(markers, positions, spacing) {
  const bounds = markers.map((m, i) => boundsAtPosition(m, positions[i]));
  for (let i = 0; i < bounds.length; i++) {
    for (let j = i + 1; j < bounds.length; j++) {
      if (rectsOverlap(bounds[i], bounds[j], spacing)) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Find the smallest radius at which markers placed on a circle (at the
 * given angles from center) do not have overlapping bounding boxes.
 *
 * Uses binary search for efficiency.
 *
 * @param {{ width: number, height: number, anchorX: number, anchorY: number }[]} markers
 * @param {{ x: number, y: number }} center
 * @param {number[]} angles - one angle per marker (radians)
 * @param {number} spacing - minimum gap between bounding boxes
 * @param {number} [maxRadius=2000] - upper bound for binary search
 * @param {number} [tolerance=0.5] - precision of the result in pixels
 * @returns {number}
 */
export function smallestNonOverlappingRadius(
  markers,
  center,
  angles,
  spacing,
  maxRadius = 2000,
  tolerance = 0.5
) {
  // If only 0 or 1 markers, no radius needed.
  if (markers.length <= 1) {
    return 0;
  }

  let lo = 0;
  let hi = maxRadius;

  // Quick check: if at maxRadius they still overlap, return maxRadius.
  const posAtMax = markers.map((_, i) => positionOnCircle(center, hi, angles[i]));
  if (anyOverlap(markers, posAtMax, spacing)) {
    return maxRadius;
  }

  while (hi - lo > tolerance) {
    const mid = (lo + hi) / 2;
    const positions = markers.map((_, i) => positionOnCircle(center, mid, angles[i]));
    if (anyOverlap(markers, positions, spacing)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return hi;
}

/**
 * Compute the distance between two points.
 * @param {{ x: number, y: number }} p1
 * @param {{ x: number, y: number }} p2
 * @returns {number}
 */
export function distance(p1, p2) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  return Math.sqrt(dx * dx + dy * dy);
}
